// All tours in the app live here, keyed by name. The TourContext picks
// one when `start(key)` is called. Each step targets a `data-testid` so
// the spotlight overlay can highlight real UI. Steps can optionally:
//   - `route`: navigate to that URL before showing the step
//   - `dispatchEvent: { name, detail }`: fire a window event when the
//     step becomes active. Pages can listen for these to switch internal
//     state (e.g. the Training Studio listens for `tour:training:set-tab`
//     and switches tabs accordingly).

export const TOURS = {
  // ── Universal app-wide intro ───────────────────────────────────
  universal: [
    {
      id: "welcome",
      target: null,
      route: "/dashboard",
      title: "Welcome to SalonFlow",
      body: "We'll walk you through the modules you use every day. It takes about a minute. You can skip or restart anytime from the Help button.",
      placement: "center",
    },
    { id: "sidebar-dashboard", target: "nav-dashboard", route: "/dashboard",
      title: "Your dashboard",
      body: "Today's KPIs — revenue, bookings, no-show risk, live team activity. Customize tiles with the Edit button on this page.",
      placement: "right" },
    { id: "calendar", target: "nav-calendar", route: "/calendar",
      title: "Book & manage appointments",
      body: "Click empty slots to create a new appointment, drag to reschedule across staff, and click any block to check the client out at the POS.",
      placement: "right" },
    { id: "clients", target: "nav-all-clients", route: "/clients",
      title: "Client CRM",
      body: "Every client lives here with a full history — appointments, transactions, color formulas, notes, and a tier score that tells you who your VIPs are.",
      placement: "right" },
    { id: "pos", target: "nav-checkout", route: "/pos",
      title: "Checkout / POS",
      body: "Take payment, attach services + retail to a stylist for commission, redeem gift cards or loyalty points, and capture tips. Card on file and real Stripe payments are both supported.",
      placement: "right" },
    { id: "staff", target: "nav-staff", route: "/staff",
      title: "Staff & permissions",
      body: "Add team members, set their role, assign their services with custom pricing, and reset PINs or passwords. Access Levels in Settings control exactly what each role can see and do.",
      placement: "right" },
    { id: "timeclock", target: "nav-time-clock", route: "/timeclock",
      title: "Time Clock & payroll",
      body: "Track every shift. Staff clock in/out from the iPad kiosk (or right from their portal), can take breaks, and submit time-off or missed-punch requests for manager approval.",
      placement: "right" },
    { id: "training", target: "nav-training", route: "/training",
      title: "Training Studio",
      body: "Run your education program: tracks, sessions, evaluations, approvals, and a quality dashboard. Use the Section Tour button on that page for a deep dive.",
      placement: "right" },
    { id: "payroll", target: "nav-payroll", route: "/payroll",
      title: "Payroll & commissions",
      body: "Variable commission tiers, tips, and hourly all roll up here. Export CSV for your payroll provider in one click.",
      placement: "right" },
    { id: "marketing", target: "nav-campaigns", route: "/marketing",
      title: "Marketing campaigns",
      body: "Send email + SMS campaigns, automated rebook nudges, birthday flows, and review requests. AI copy generation is built in.",
      placement: "right" },
    { id: "settings", target: "nav-settings", route: "/settings",
      title: "Settings & branding",
      body: "Tune your logo, colors, services, locations, business hours, and the Onboarding Tour toggle (where you turn this guide off for your team).",
      placement: "right" },
    { id: "help-button", target: "tour-help-button", route: null,
      title: "Replay anytime",
      body: "Tap the Help button in the bottom-right of any page to restart this tour or jump to a specific module. You're all set — happy salon-running!",
      placement: "left" },
  ],

  // ── Training Studio deep dive ──────────────────────────────────
  // Walks through every tab + a few key features inside the
  // Training module. Pages listen for `tour:training:set-tab` to
  // switch tabs before each step.
  training: [
    {
      id: "tr-welcome",
      target: null,
      route: "/training",
      title: "Training Studio deep dive",
      body: "Let's walk through everything in here: how to build a curriculum, schedule sessions, score evaluations, certify staff, and run the quality dashboard.",
      placement: "center",
    },
    { id: "tr-dashboard", target: "tab-dashboard", route: "/training",
      title: "Dashboard tab",
      body: "Your at-a-glance snapshot: track count, sessions logged, evaluations submitted, certified staff, and who's currently in the pipeline.",
      placement: "bottom",
      dispatchEvent: { name: "tour:training:set-tab", detail: { tab: "dashboard" } } },
    { id: "tr-tracks", target: "tab-tracks",
      title: "Tracks — your curriculum",
      body: "A 'Track' is a service-specific training path (e.g. Balayage, Men's Haircut, Lash Lift). Each track defines competencies, quality standards, skill level, and the approval steps a trainee must pass to be certified.",
      placement: "bottom",
      dispatchEvent: { name: "tour:training:set-tab", detail: { tab: "tracks" } } },
    { id: "tr-new-track", target: "create-track-btn",
      title: "Create a new track",
      body: "Click here to define a new training path. You'll add competencies with weights (e.g. tension 30%, sectioning 25%), quality standards, and what's required at each approval stage.",
      placement: "bottom" },
    { id: "tr-sessions", target: "tab-sessions",
      title: "Sessions — scheduled classes",
      body: "Anything from a classroom lecture to a hands-on model session, a shadow shift, or a remediation. Each session has an agenda, attendees, optional model info, and an educator.",
      placement: "bottom",
      dispatchEvent: { name: "tour:training:set-tab", detail: { tab: "sessions" } } },
    { id: "tr-new-session", target: "create-session-btn",
      title: "Schedule a session",
      body: "Pick a track + session type (classroom, demo, shadow, model, quality check, certification, remediation), set the date/time, assign trainees, and write the agenda.",
      placement: "bottom" },
    { id: "tr-evaluations", target: "tab-evaluations",
      title: "Evaluations — score your trainees",
      body: "After a hands-on session, score a trainee against the track's competencies (1–5 stars), capture before/after photos, log strengths and improvements, and decide Pass / Needs Work / Fail.",
      placement: "bottom",
      dispatchEvent: { name: "tour:training:set-tab", detail: { tab: "evaluations" } } },
    { id: "tr-eval-search", target: "eval-search-input",
      title: "Search & filter",
      body: "As your evaluation history grows, use search + the status pills + service / trainee dropdowns + sort to slice the list. Hover any card to reveal inline Edit + Delete buttons.",
      placement: "bottom" },
    { id: "tr-eval-status", target: "eval-status-pass",
      title: "Filter by outcome",
      body: "Quickly see only Passes, only Needs Work, or only Fails. The number next to each pill is the count for that status across the entire list.",
      placement: "bottom" },
    { id: "tr-new-eval", target: "create-eval-btn",
      title: "Run a new evaluation",
      body: "Pick the track and trainee — the form auto-populates with every competency you defined on that track, ready for star ratings and notes.",
      placement: "bottom" },
    { id: "tr-approvals", target: "tab-approvals",
      title: "Approvals pipeline",
      body: "Each trainee progresses through a status ladder per service: Not Ready → Model Approved → Supervised → Independent → Certified. Promote them as they hit milestones.",
      placement: "bottom",
      dispatchEvent: { name: "tour:training:set-tab", detail: { tab: "approvals" } } },
    { id: "tr-profiles", target: "tab-profiles",
      title: "Employee training profiles",
      body: "One card per team member showing every service they're approved for, their evaluation count, average score, upcoming sessions, and recent feedback. Great for 1-on-1 prep.",
      placement: "bottom",
      dispatchEvent: { name: "tour:training:set-tab", detail: { tab: "profiles" } } },
    { id: "tr-library", target: "tab-library",
      title: "Content library",
      body: "SOPs, guides, scripts, references, videos — link them to specific tracks so trainees see exactly what to study before their hands-on session.",
      placement: "bottom",
      dispatchEvent: { name: "tour:training:set-tab", detail: { tab: "library" } } },
    { id: "tr-quality", target: "tab-quality",
      title: "Quality dashboard",
      body: "Salon-wide readiness: average evaluation scores, service readiness matrix, common coaching areas, and a per-provider table so you can see exactly where to invest your educator's time.",
      placement: "bottom",
      dispatchEvent: { name: "tour:training:set-tab", detail: { tab: "quality" } } },
    { id: "tr-finish", target: null,
      title: "That's the Training Studio",
      body: "Tracks set the curriculum, Sessions schedule the practice, Evaluations score the outcome, and Approvals + Profiles + Quality close the loop on certification. Tap the Section Tour button anytime to replay this.",
      placement: "center" },
  ],
};

// Backwards-compat export for the original universal tour.
export const TOUR_STEPS = TOURS.universal;
